import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Mic, CheckCircle, Check, X, Sparkles, Loader, RotateCw, Copy,
    Send, MessageSquare, ArrowLeft, Share2, User,
    UploadCloud, Trash2, Plus, Disc, ChevronRight,
    FileText, Monitor, Video, Maximize2, Link, Upload, Play
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { doc, updateDoc, setDoc, onSnapshot, query, collection, where, limit } from 'firebase/firestore';
// NOTE: generateEpisodeBriefing, generateEpisodeTopics, generateShowNotes were moved to repurpose phase
// These functions will be reimplemented in the repurpose phase
import guestPortalService from '../services/guestPortalService';
import webSearchService from '../services/webSearchService';
import { getDocs } from 'firebase/firestore';
import TranscriptionModal from '../components/episodes/TranscriptionModal';
import StudioBadge from '../components/ui/StudioBadge';
import AiModule from '../components/episodes/AiModule';

// --- STUDIO UI COMPONENTS (Redesigned for Sunny Studio) ---
import StudioLayout from '../components/studio/StudioLayout';
import PlanCanvas from '../components/studio/plan/PlanCanvas';
import VizardClipStudio from '../components/studio/vizard/VizardClipStudio';
import RepurposeSidebar from '../components/studio/repurpose/RepurposeSidebar';
import CategoryTabs from '../components/studio/repurpose/CategoryTabs';
import ContentTypeGrid from '../components/studio/repurpose/ContentTypeGrid';
import QuickActionsBar from '../components/studio/repurpose/QuickActionsBar';

// StudioBadge (imported)

const TabButton = ({ children, onClick, active, icon: Icon }) => (
    <button
        onClick={onClick}
        className={`relative px-4 py-2.5 rounded-xl flex items-center gap-2 transition-all duration-200 font-bold text-xs uppercase tracking-wide
        ${active
                ? 'bg-white text-indigo-600 shadow-sm ring-1 ring-black/5'
                : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
            }`}
    >
        {Icon && <Icon size={14} className={active ? 'text-indigo-600' : 'text-gray-400'} />}
        {children}
    </button>
);

const TapeReelLoader = () => (
    <div className="relative w-16 h-16 flex items-center justify-center opacity-90">
        <Disc size={64} className="text-gray-200 absolute animate-spin-slow" strokeWidth={1} />
        <div className="w-16 h-0.5 bg-gray-300 absolute transform rotate-45"></div>
        <div className="w-16 h-0.5 bg-gray-300 absolute transform -rotate-45"></div>
        <div className="w-6 h-6 bg-white rounded-full border-2 border-indigo-100 z-10 shadow-sm"></div>
    </div>
);

// AiModule (imported)

const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm animate-fade-in" onClick={onClose}></div>
            <div className="bg-white rounded-[32px] p-8 max-w-sm w-full shadow-2xl relative z-10 animate-bounce-slow transform scale-100 hover:scale-[1.02] transition-transform duration-300 border border-white/60">
                <div className="w-32 h-32 mb-4 mx-auto relative -mt-16">
                    <div className="absolute inset-x-4 bottom-0 h-4 bg-black/10 blur-xl rounded-full"></div>
                    <img
                        src="/mascot/mascot_warning.png"
                        alt="Concerned Mascot"
                        className="w-full h-full object-contain relative z-10 drop-shadow-2xl"
                    />
                </div>
                <h3 className="text-2xl font-black text-center text-gray-900 mb-2 font-['Outfit']">{title}</h3>
                <p className="text-center text-gray-500 mb-8 font-medium leading-relaxed">
                    {message}
                </p>
                <div className="flex gap-3">
                    <button
                        onClick={onClose}
                        className="flex-1 py-3.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-2xl font-bold transition-all active:scale-95"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        className="flex-1 py-3.5 bg-indigo-600 hover:bg-indigo-700 text-indigo-50 rounded-2xl font-bold shadow-lg shadow-indigo-200 transition-all active:scale-95"
                    >
                        Disable
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- MAIN PAGE COMPONENT ---

// Helper to get full name from standardized schema (firstName/lastName) or legacy field
const getFullName = (data) => {
    if (data?.firstName && data?.lastName) {
        return `${data.firstName} ${data.lastName}`;
    }
    return data?.founderName || data?.guestName || 'Unknown Guest';
};

// Helper to normalize status (handle both lowercase and Title Case)
const normalizeStatus = (status) => {
    if (!status) return 'scheduled';
    if (typeof status === 'object' && status !== null) {
        return status.status || status.stage || 'scheduled';
    }
    const lower = status.toLowerCase();
    const statusMap = {
        'scheduled': 'scheduled',
        'prepped': 'prepped',
        'recorded': 'recorded',
        'published': 'published',
        'post-production': 'post-production'
    };
    return statusMap[lower] || lower;
};

const EpisodePage = ({ episode, onBack, onEpisodeStatusUpdate, onViewLead, user, db, settings }) => {
    // --- STATE MANAGEMENT ---
    const [status] = useState(() => {
        const raw = episode.status || episode.productionStatus;
        return normalizeStatus(raw);
    });
    const [activeTab, setActiveTab] = useState('preparation');

    // Repurpose phase state
    const [activeCategory, setActiveCategory] = useState('video');

    // Sidebar collapse state (persisted to localStorage)
    const [planSidebarCollapsed, setPlanSidebarCollapsed] = useState(() => {
        try {
            const saved = localStorage.getItem('podguestly-plan-sidebar-collapsed');
            return saved === 'true';
        } catch {
            return false;
        }
    });

    const [repurposeSidebarCollapsed, setRepurposeSidebarCollapsed] = useState(() => {
        try {
            const saved = localStorage.getItem('podguestly-repurpose-sidebar-collapsed');
            return saved === 'true';
        } catch {
            return false;
        }
    });

    // Persist sidebar states
    useEffect(() => {
        try {
            localStorage.setItem('podguestly-plan-sidebar-collapsed', String(planSidebarCollapsed));
        } catch { }
    }, [planSidebarCollapsed]);

    useEffect(() => {
        try {
            localStorage.setItem('podguestly-repurpose-sidebar-collapsed', String(repurposeSidebarCollapsed));
        } catch { }
    }, [repurposeSidebarCollapsed]);

    // Data States
    const [briefing, setBriefing] = useState('');
    const [topics, setTopics] = useState('');
    const [showNotes, setShowNotes] = useState(null);
    // Add local transcript state to ensure persistence after refresh
    const [transcript, setTranscript] = useState(episode.transcript || null);
    const [viralClips, setViralClips] = useState(episode.viralClips || null);
    const [guestBio, setGuestBio] = useState(episode.guestBio || '');
    const [episodePlan, setEpisodePlan] = useState(episode.plan || null);

    // Checklist State
    const [checklist, setChecklist] = useState([
        { id: 1, text: 'Confirm recording date', completed: false },
        { id: 2, text: 'Tech check email sent', completed: false },
        { id: 3, text: 'Topics finalized', completed: false },
    ]);

    // Loading & UI States
    const [dataLoaded, setDataLoaded] = useState(false);
    const [isHydrating, setIsHydrating] = useState(true); // Prevents flash of stale data
    const [modalContent, setModalContent] = useState(null);
    const [isGeneratingBriefing, setIsGeneratingBriefing] = useState(false);
    const [isGeneratingTopics, setIsGeneratingTopics] = useState(false);
    const [isGeneratingShowNotes, setIsGeneratingShowNotes] = useState(false);
    const [isGeneratingViralClips, setIsGeneratingViralClips] = useState(false);
    const [showUrlModal, setShowUrlModal] = useState(false);
    const [showTimestamps, setShowTimestamps] = useState(episode.preferences?.showTimestamps ?? true);

    const saveEpisodeToFirestore = useCallback(async (field, value) => {
        if (!user || !episode || !episode.id || !db) return;
        try {
            const episodeRef = doc(db, `tenants/${user.uid}/content/episodes/items`, episode.id);
            await updateDoc(episodeRef, { [field]: value, lastUpdated: new Date().toISOString() });

            // SYNC TO CONTACT: Update the source contact document to prevent flicker
            // The episode.id corresponds to the contact ID in the contacts collection
            const syncableFields = ['headshotUrl', 'founderName', 'firstName', 'lastName', 'company', 'companyName'];
            if (syncableFields.includes(field)) {
                try {
                    const contactRef = doc(db, `tenants/${user.uid}/contacts`, episode.id);
                    await updateDoc(contactRef, {
                        [field]: value,
                        lastUpdated: new Date().toISOString()
                    });
                } catch (syncError) {
                    // Non-critical: episode is still saved
                }
            }
        } catch (error) {
        }
    }, [user, episode, db]);


    const toggleTimestamps = async () => {
        const newValue = !showTimestamps;
        setShowTimestamps(newValue);
        // Persist to Firestore
        if (episode?.id && user?.uid) {
            try {
                // Determine if we need to merge with existing preferences
                const newPreferences = { ...(episode.preferences || {}), showTimestamps: newValue };
                await saveEpisodeToFirestore('preferences', newPreferences);
            } catch (err) {
            }
        }
    };

    // Transcription State
    const [transcriptionJob, setTranscriptionJob] = useState(null);
    const [showTranscriptionModal, setShowTranscriptionModal] = useState(false);
    // Structure: { id, status: 'processing' | 'completed' | 'failed', progress: 0-100, filename, type }

    // Portal State
    const [guestPortal, setGuestPortal] = useState({ loading: true, portal: null, error: '' });

    // --- TRANSCRIPTION POLLING ---
    useEffect(() => {
        if (!transcriptionJob || transcriptionJob.status !== 'processing' || !transcriptionJob.id) return;


        const pollStatus = async () => {
            try {
                const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
                const token = user ? await user.getIdToken() : '';

                const response = await fetch(`${apiBaseUrl}/api/transcription/status/${transcriptionJob.id}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (response.ok) {
                    const resData = await response.json();
                    const statusData = resData.data;

                    const innerData = statusData?.data || statusData; // Handle both {data:{...}} and flat {...} just in case
                    const currentStatus = innerData.status || 'processing';
                    const currentProgress = innerData.progress;

                    // 'done' is the official status for completion per docs
                    if (currentStatus === 'done' || currentStatus === 'completed' || currentStatus === 'success') {
                        let transcriptText = innerData.result || innerData.transcript;

                        if (typeof transcriptText === 'object' && transcriptText !== null) {
                            transcriptText = JSON.stringify(transcriptText, null, 2);
                        }


                        setTranscriptionJob(prev => ({ ...prev, status: 'completed', progress: 100, transcript: transcriptText }));

                        setTranscriptionJob(prev => ({ ...prev, status: 'completed', progress: 100, transcript: transcriptText }));

                        if (transcriptText) {
                            saveEpisodeToFirestore('transcript', transcriptText);
                            setTranscript(transcriptText); // Update local state
                            if (onEpisodeStatusUpdate) {
                                onEpisodeStatusUpdate({ ...episode, transcript: transcriptText });
                            }
                            // Auto-close modal to reveal the In-Page Transcript View
                            setTimeout(() => setShowTranscriptionModal(false), 1500); // 1.5s delay to show 100% completion in modal first
                        }
                    } else if (currentStatus === 'failed' || currentStatus === 'error') {
                        setTranscriptionJob(prev => ({ ...prev, status: 'failed' }));
                    } else {
                        // Still processing
                        setTranscriptionJob(prev => ({
                            ...prev,
                            // Use real progress if available (0-100), otherwise fallback to visual cycling
                            progress: (currentProgress !== undefined && currentProgress !== null)
                                ? currentProgress
                                : (prev.progress >= 100 ? 0 : (prev.progress || 0) + 5)
                        }));
                    }
                }
            } catch (err) {
            }
        };

        const interval = setInterval(pollStatus, 3000); // Poll every 3 seconds for faster updates
        return () => clearInterval(interval);


    }, [transcriptionJob, user, saveEpisodeToFirestore, onEpisodeStatusUpdate]);
    // Restore job state from Firestore if page reloads (Optional enhancement, but simplifying for now)
    // We would need to save 'transcriptionJobId' and 'transcriptionJobStatus' to firestore to support reload persistence.

    // --- LOGIC & EFFECTS (Keep existing logic mostly intact, just modernized) ---




    const [headshotUrl, setHeadshotUrl] = useState(episode.headshotUrl || '');
    const [guestName, setGuestName] = useState(getFullName(episode));
    const [firstName, setFirstName] = useState(episode.firstName || '');
    const [lastName, setLastName] = useState(episode.lastName || '');

    // Create a merged episode object that uses Firestore-synced state values
    // This ensures child components receive the latest data, not stale prop data
    // Defined early so it can be used in handlers defined later
    const syncedEpisode = {
        ...episode,
        headshotUrl,
        founderName: guestName, // Keep for backward compatibility
        firstName,
        lastName,
        guestBio,
        transcript,
        viralClips,
        plan: episodePlan,
        showNotes,
        briefing,
        topics
    };

    // Initial Load & Real-time Sync
    useEffect(() => {
        if (!user || !episode || !episode.id || !db) return;

        let unsubscribe = () => { };

        try {
            const episodeRef = doc(db, `tenants/${user.uid}/content/episodes/items`, episode.id);

            unsubscribe = onSnapshot(episodeRef, async (episodeDoc) => {
                if (episodeDoc.exists()) {
                    const data = episodeDoc.data();
                    setBriefing(data.briefing || '');
                    setTopics(data.topics || '');
                    if (data.showNotes) setShowNotes(data.showNotes);

                    // Live sync guest info - use Firestore data as source of truth
                    setHeadshotUrl(data.headshotUrl || '');
                    // Handle both standardized and legacy name fields
                    if (data.firstName) setFirstName(data.firstName);
                    if (data.lastName) setLastName(data.lastName);
                    setGuestName(getFullName(data));
                    if (data.guestBio) setGuestBio(data.guestBio);

                    // Hydrate transcript from Firestore
                    setTranscript(data.transcript || null);
                    if (data.viralClips) setViralClips(data.viralClips);
                    if (data.plan) setEpisodePlan(data.plan);
                    // Hydrate preferences
                    if (data.preferences?.showTimestamps !== undefined) {
                        setShowTimestamps(data.preferences.showTimestamps);
                    }
                    if (data.checklist) setChecklist(data.checklist);
                } else {
                    // Create if missing - use standardized schema
                    await setDoc(episodeRef, {
                        id: episode.id,
                        firstName: episode.firstName || '',
                        lastName: episode.lastName || '',
                        founderName: getFullName(episode), // Keep for backward compatibility
                        companyName: episode.companyName || 'Company',
                        createdAt: new Date().toISOString()
                    }, { merge: true });
                }
                setDataLoaded(true);
                setIsHydrating(false); // Data is now ready, clear loading state
            }, (error) => {
                setIsHydrating(false); // Clear loading even on error to prevent stuck state
            });

        } catch (error) {
        }

        return () => unsubscribe();
    }, [user, episode, db]);

    // Portal Listener (Same as before)
    useEffect(() => {
        let unsubscribe = () => { };
        const setupListener = async () => {
            if (!user || !episode || !episode.id || !db) return;
            try {
                const q = query(
                    collection(db, 'tenants', user.uid, 'communications', 'portals', 'items'),
                    where('episodeId', '==', episode.id),
                    limit(1)
                );
                unsubscribe = onSnapshot(q, (snapshot) => {
                    if (!snapshot.empty) {
                        const portalDoc = snapshot.docs[0];
                        setGuestPortal({ loading: false, portal: { id: portalDoc.id, ...portalDoc.data() } });
                    } else {
                        setGuestPortal({ loading: false, portal: null, error: '' });
                    }
                });
            } catch (err) {
            }
        };
        setupListener();
        return () => unsubscribe();
    }, [user, episode, db]);

    const handleCreatePortal = async () => {
        if (!user || !episode) return;
        setGuestPortal(prev => ({ ...prev, loading: true }));
        try {
            await guestPortalService.createPortal(episode);
        } catch (error) {
            setGuestPortal(prev => ({ ...prev, loading: false, error: 'Failed to create portal.' }));
        }
    };

    const [showDisableConfirm, setShowDisableConfirm] = useState(false);

    const handleTogglePortalStatus = async (confirmed = false) => {
        if (!guestPortal.portal || !guestPortal.portal.token) return;

        const isActive = guestPortal.portal.status === 'active';

        if (isActive && !confirmed) {
            setShowDisableConfirm(true);
            return;
        }

        const action = isActive ? 'disable' : 'enable';

        setGuestPortal(prev => ({ ...prev, loading: true }));
        try {
            if (isActive) {
                await guestPortalService.deactivatePortal(guestPortal.portal.token);
            } else {
                await guestPortalService.activatePortal(guestPortal.portal.token);
            }
        } catch (error) {
            alert(`Failed to ${action} portal.`);
            setGuestPortal(prev => ({ ...prev, loading: false }));
        } finally {
            setShowDisableConfirm(false);
        }
    };


    // --- HOST CHAT LOGIC ---
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [hostMessage, setHostMessage] = useState('');
    const [sendingHostMessage, setSendingHostMessage] = useState(false);
    const hostChatEndRef = useRef(null);
    const hostChatContainerRef = useRef(null);

    const scrollChatToBottom = () => {
        if (hostChatContainerRef.current) {
            hostChatContainerRef.current.scrollTop = hostChatContainerRef.current.scrollHeight;
        }
    };

    // Auto-scroll when messages change or chat opens
    useEffect(() => {
        if (isChatOpen) {
            setTimeout(scrollChatToBottom, 100);
        }
    }, [guestPortal.portal?.messages, isChatOpen]);

    const handleSendHostMessage = async () => {
        if (!hostMessage.trim() || !guestPortal.portal?.token || !user) return;

        setSendingHostMessage(true);
        try {
            const token = guestPortal.portal.token;
            // Direct write to Firestore as Host (Owner)
            const portalRef = doc(db, 'tenants', user.uid, 'communications', 'portals', 'items', token);

            // Standard message object
            const newMessage = {
                text: hostMessage.trim().slice(0, 2000),
                sender: 'host',
                timestamp: new Date().toISOString() // or Firestore Timestamp
            };

            await updateDoc(portalRef, {
                messages: (guestPortal.portal.messages || []).concat(newMessage)
            });

            setHostMessage('');
            scrollChatToBottom();
        } catch (error) {
            alert('Failed to send message. Please try again.');
        } finally {
            setSendingHostMessage(false);
        }
    };

    // --- HYDRATION EFFECT: Sync Dossier from Lead if missing OR if Lead has better data ---
    useEffect(() => {
        const syncDossierFromLead = async () => {
            // Use state values (guestName, guestBio) instead of prop values for current data
            if (user && episode.id && dataLoaded && guestName) {
                try {
                    // Try to find the lead by name - check both standardized and legacy fields
                    let q = query(
                        collection(db, `tenants/${user.uid}/contacts`),
                        where('firstName', '==', firstName || episode.firstName),
                        limit(1)
                    );

                    let snap = await getDocs(q);

                    // If not found by firstName, try by founderName (legacy)
                    if (snap.empty && (episode.founderName || guestName)) {
                        q = query(
                            collection(db, `tenants/${user.uid}/contacts`),
                            where('founderName', '==', episode.founderName || guestName),
                            limit(1)
                        );
                        snap = await getDocs(q);
                    }

                    if (snap.empty) {
                        return;
                    }


                    const leadData = snap.docs[0].data();
                    // Check for bio or guestBio on the lead
                    const sourceDossier = leadData.bio || leadData.guestBio;
                    // Use state value for current dossier
                    const currentDossier = guestBio;

                    // Update if:

                    // 1. We have no dossier currently
                    // 2. The source dossier is significantly longer (at least 20 chars)
                    if (sourceDossier) {
                        const shouldUpdate = !currentDossier || (sourceDossier.length > (currentDossier.length + 20));

                        if (shouldUpdate) {
                            await saveEpisodeToFirestore('guestBio', sourceDossier);
                        } else {
                        }
                    }
                } catch (error) {
                }
            }
        };
        syncDossierFromLead();
    }, [dataLoaded, guestBio, guestName, user, db, saveEpisodeToFirestore, episode.id]);

    // AI Handlers
    const handleResearchOrBriefing = async () => {
        setIsGeneratingBriefing(true);
        try {
            // BRANCH A: DEEP RESEARCH (If no dossier exists)
            // Use state value (guestBio) instead of prop (episode.guestBio) for current data
            if (!guestBio) {

                const researchData = await webSearchService.getPodcasterResearch(
                    guestName || getFullName(episode),
                    episode.companyName,
                    user
                );

                // 1. Save to Episode (this will trigger re-render and switch button to "Guest Briefing")
                const updatedGuestBio = researchData;
                await saveEpisodeToFirestore('guestBio', updatedGuestBio);

                // 2. Sync to Linked Contact/Lead
                let contactId = episode.leadId;
                if (!contactId && user) {
                    // Try by firstName first (standardized), then by founderName (legacy)
                    let q = query(
                        collection(db, `tenants/${user.uid}/contacts`),
                        where('firstName', '==', firstName || episode.firstName),
                        limit(1)
                    );
                    let snap = await getDocs(q);

                    if (snap.empty) {
                        q = query(
                            collection(db, `tenants/${user.uid}/contacts`),
                            where('founderName', '==', guestName || getFullName(episode)),
                            limit(1)
                        );
                        snap = await getDocs(q);
                    }
                    if (!snap.empty) contactId = snap.docs[0].id;
                }

                if (contactId && user) {
                    const contactRef = doc(db, `tenants/${user.uid}/contacts`, contactId);
                    await updateDoc(contactRef, {
                        bio: updatedGuestBio,
                        guestBio: updatedGuestBio,
                        lastUpdated: new Date().toISOString()
                    });
                }

                // Optional: Auto-trigger briefing generation after research?
                // For now, let's let the user click "Guest Briefing" to be explicit, or we can chain it.
                // User said: "then deep research button changes to host briefing" -> implying manual second step is fine.

            } else {
                // BRANCH B: GUEST BRIEFING (If dossier exists)
                // TODO: Reimplement in repurpose phase
                // const result = await generateEpisodeBriefing(settings, syncedEpisode);
                // setBriefing(result);
                // await saveEpisodeToFirestore('briefing', result);
                console.log('Episode briefing will be reimplemented in repurpose phase');
            }

        } catch (error) {
            alert("Process failed. Please try again.");
        } finally {
            setIsGeneratingBriefing(false);
        }
    };

    // --- TRANSCRIPTION UPLOAD HANDLER ---
    const fileInputRef = useRef(null);
    const [isUploadingMaster, setIsUploadingMaster] = useState(false);

    const handleMasterUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setIsUploadingMaster(true);
        setShowTranscriptionModal(true); // Open immediately

        try {
            const token = user ? await user.getIdToken() : '';
            const formData = new FormData();
            formData.append('file', file);

            // Use configured API URL or default
            const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';

            const response = await fetch(`${apiBaseUrl}/api/transcription/upload-master`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Upload failed');
            }

            const data = await response.json();

            // Extract Job ID
            // Structure: { data: { request_id: ... } }
            // Backend wraps response in { data: ... }

            const apiResponse = data.data;
            const jobId = apiResponse?.data?.request_id || apiResponse?.request_id || apiResponse?.id || 'mock-id-' + Date.now();


            setTranscriptionJob({
                id: jobId,
                status: 'processing',
                progress: 0,
                filename: file.name,
                type: file.type.startsWith('video') ? 'video' : 'audio'
            });

            // Persist ID to Firestore
            await saveEpisodeToFirestore('transcriptionJobId', jobId);

            // alert("Master file uploaded! Transcription started in background."); // Removing alert for smoother UI

        } catch (error) {
            alert(`Upload failed: ${error.message}`);
            setShowTranscriptionModal(false);
        } finally {
            setIsUploadingMaster(false);
            // Reset input
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleUrlUpload = () => {
        setShowUrlModal(true);
    };

    const submitUrlUpload = async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const url = formData.get('urlInput');

        if (!url) return;

        setShowUrlModal(false); // Close modal right away
        setIsUploadingMaster(true);
        setShowTranscriptionModal(true); // Open progress modal

        try {
            const token = user ? await user.getIdToken() : '';
            const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';

            const response = await fetch(`${apiBaseUrl}/api/transcription/upload-url`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ url })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'URL Upload failed');
            }

            const data = await response.json();

            const deResponse = data.data;
            const jobId = deResponse?.data?.request_id || deResponse?.request_id || deResponse?.id || 'mock-id-' + Date.now();


            setTranscriptionJob({
                id: jobId,
                status: 'processing',
                progress: 0,
                filename: url,
                type: 'video' // Usually URL uploads are videos in this context
            });

            await saveEpisodeToFirestore('transcriptionJobId', jobId);

        } catch (error) {
            alert(`URL submission failed: ${error.message}`);
            setShowTranscriptionModal(false);
        } finally {
            setIsUploadingMaster(false);
        }
    };

    const handleGenerateTopics = async () => {
        setIsGeneratingTopics(true);
        try {
            // TODO: Reimplement in repurpose phase
            // const result = await generateEpisodeTopics(settings, syncedEpisode, 8);
            // setTopics(result);
            // await saveEpisodeToFirestore('topics', result);
            console.log('Episode topics will be reimplemented in repurpose phase');
        } finally { setIsGeneratingTopics(false); }
    };

    const handleGenerateShowNotes = async () => {
        setIsGeneratingShowNotes(true);
        try {
            // TODO: Reimplement in repurpose phase
            // const result = await generateShowNotes(settings, syncedEpisode, topics);
            // setShowNotes(result);
            // await saveEpisodeToFirestore('showNotes', result);
            console.log('Show notes will be reimplemented in repurpose phase');
        } finally { setIsGeneratingShowNotes(false); }
    };



    const handleSavePlan = useCallback(async (content) => {
        setEpisodePlan(content);
        await saveEpisodeToFirestore('plan', content);
    }, [saveEpisodeToFirestore]);

    // Tasks logic
    const toggleTask = (id) => {
        const newChecklist = checklist.map(t => t.id === id ? { ...t, completed: !t.completed } : t);
        setChecklist(newChecklist);
        if (dataLoaded) saveEpisodeToFirestore('checklist', newChecklist);
    };

    const [isAddingTask, setIsAddingTask] = useState(false);
    const [newTaskText, setNewTaskText] = useState('');

    const handleAddTask = () => {
        if (!newTaskText.trim()) return;
        const newTask = {
            id: Date.now(),
            text: newTaskText,
            completed: false
        };
        const newChecklist = [...checklist, newTask];
        setChecklist(newChecklist);
        setNewTaskText('');
        setIsAddingTask(false);
        if (dataLoaded) saveEpisodeToFirestore('checklist', newChecklist);
    };

    const handleDeleteTask = (id) => {
        const newChecklist = checklist.filter(t => t.id !== id);
        setChecklist(newChecklist);
        if (dataLoaded) saveEpisodeToFirestore('checklist', newChecklist);
    };

    const removeTimestamps = (text) => {
        if (!text) return '';
        // Matches [00:00], [00:00:00], (00:00), 00:00 etc, plus optional trailing " - " or " " 
        return text.replace(/\[?\d{1,2}:\d{2}(:\d{2})?\]?(\s*-\s*|\s+)?/g, '');
    };

    const formatShowNotesToMarkdown = (notes) => {
        if (!notes) return '';
        try {
            // Handle if notes is string (might be double encoded)
            const data = typeof notes === 'string' ? JSON.parse(notes) : notes;

            let md = '';

            // Titles
            if (data.titles && data.titles.length > 0) {
                md += `# ${data.titles[0]}\n\n`;
                if (data.titles.length > 1) {
                    md += `**Alternative Titles:**\n${data.titles.slice(1).map(t => `* ${t}`).join('\n')}\n\n`;
                }
            }

            // Summary
            if (data.summary) {
                md += `## 📝 Summary\n${data.summary}\n\n`;
            }

            // Timestamps
            if (data.timestamps && data.timestamps.length > 0) {
                md += `## ⏱️ Timestamps\n`;
                md += data.timestamps.map(t => `* **${t.time}** - ${t.label}`).join('\n');
                md += `\n\n`;
            }

            // Takeaways
            if (data.takeaways && data.takeaways.length > 0) {
                md += `## 🚀 Key Takeaways\n`;
                md += data.takeaways.map(t => `* ${t}`).join('\n');
                md += `\n\n`;
            }

            // Quotes
            if (data.keyQuotes && data.keyQuotes.length > 0) {
                md += `## 💬 Memorable Quotes\n`;
                md += data.keyQuotes.map(q => `> "${q}"`).join('\n\n');
                md += `\n\n`;
            }

            // Resources
            if (data.resources && data.resources.length > 0) {
                md += `## 🔗 Resources\n`;
                md += data.resources.map(r => `* ${r}`).join('\n');
                md += `\n\n`;
            }

            return md;
        } catch (e) {
            return typeof notes === 'string' ? notes : JSON.stringify(notes, null, 2);
        }
    };

    // --- STUDIO PHASE MAPPING ---
    // Map legacy 'preparation' tab to new 'plan' phase
    const currentPhase = activeTab === 'preparation' ? 'plan' :
        activeTab === 'post' ? 'repurpose' :
            activeTab;

    const handlePhaseChange = (phaseId) => {
        // Map back to legacy tab state where needed, or just use the new ID
        // If we strictly want to support the old state names for now:
        if (phaseId === 'plan') setActiveTab('preparation');
        else if (phaseId === 'repurpose') setActiveTab('post');
        else setActiveTab(phaseId);
    };

    // --- PORTAL LOGIC ---
    // Note: We use guestPortal state (defined at line 178) which is kept in sync via Firestore listener
    // The toggle function updates the portal status through the service, and the listener updates the state

    const handleTogglePortal = async (shouldActivate) => {
        if (!episode?.id) return;

        try {
            if (shouldActivate) {
                // If portal exists, activate it. If not, create it.
                if (guestPortal.portal) {
                    await guestPortalService.activatePortal(guestPortal.portal.token);
                    // State will be updated by the Firestore listener
                } else {
                    await guestPortalService.createPortal(syncedEpisode, {
                        guestName: guestName || getFullName(episode),
                        guestTitle: episode.companyName || ''
                    });
                    // State will be updated by the Firestore listener
                }
            } else {
                // Deactivate
                if (guestPortal.portal) {
                    await guestPortalService.deactivatePortal(guestPortal.portal.token);
                    // State will be updated by the Firestore listener
                }
            }
        } catch (err) {
            console.error("Failed to toggle portal", err);
            alert("Failed to update portal status");
        }
    };

    const portalUrl = guestPortal.portal ? `${window.location.origin}/portal/${guestPortal.portal.token}` : null;
    const isPortalActive = guestPortal.portal?.status === 'active';

    return (
        <>
            {/* Loading overlay while hydrating from Firestore - prevents flash of stale data */}
            {isHydrating && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-white/80 backdrop-blur-sm">
                    <div className="flex flex-col items-center gap-4">
                        <TapeReelLoader />
                        <p className="text-sm font-medium text-gray-500 animate-pulse">Loading episode...</p>
                    </div>
                </div>
            )}
            <StudioLayout
                activePhase={currentPhase}
                onPhaseChange={handlePhaseChange}
                user={user}
                episode={syncedEpisode}
                db={db}
                onNavigateBack={onBack}
            >
                {currentPhase === 'plan' && (
                    <div className="h-full animate-fade-in">
                        <PlanCanvas
                            episode={syncedEpisode}
                            user={user}
                            db={db}
                            isPortalActive={isPortalActive}
                            onTogglePortal={handleTogglePortal}
                            portalUrl={portalUrl}
                            plan={episodePlan}
                            onSavePlan={handleSavePlan}
                            settings={settings}
                            isSidebarCollapsed={planSidebarCollapsed}
                            onToggleSidebar={() => setPlanSidebarCollapsed(!planSidebarCollapsed)}
                        />
                    </div>
                )}

                {currentPhase === 'repurpose' && (
                    <div className="relative h-full flex flex-col md:flex-row bg-[#FAFAFC]">
                        {/* Left Panel: Content Source Sidebar */}
                        <div className="flex-none lg:flex-none overflow-visible">
                            <RepurposeSidebar
                                episode={syncedEpisode}
                                user={user}
                                db={db}
                                hasVideo={false}
                                hasTranscript={false}
                                hasAudio={false}
                                isCollapsed={repurposeSidebarCollapsed}
                                onToggleCollapse={() => setRepurposeSidebarCollapsed(!repurposeSidebarCollapsed)}
                                onUploadComplete={(asset) => {
                                    console.log('[Repurpose] Asset uploaded:', asset);
                                    // Refresh episode data or update state as needed
                                }}
                            />
                        </div>

                        {/* Right Panel: Content Grid */}
                        <div className="flex-1 min-w-0 h-full overflow-y-auto bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 relative">
                            {/* Category Tabs */}
                            <CategoryTabs
                                activeCategory={activeCategory}
                                onCategoryChange={setActiveCategory}
                            />

                            {/* Content Type Grid */}
                            <ContentTypeGrid
                                activeCategory={activeCategory}
                                episode={syncedEpisode}
                                planContent={episodePlan}
                            />

                            {/* Quick Actions Bar - Sticky Bottom */}
                            <QuickActionsBar
                                selectedCount={3}
                                totalCount={8}
                            />
                        </div>
                    </div>
                )}

                {currentPhase === 'distribute' && (
                    <div className="flex flex-col items-center justify-center h-full text-center p-8 animate-fade-in">
                        <div className="w-24 h-24 bg-purple-50 rounded-full flex items-center justify-center mb-6">
                            <Sparkles size={40} className="text-purple-600" />
                        </div>
                        <h3 className="text-2xl font-bold text-slate-900 font-['Outfit'] mb-2">
                            Launchpad
                        </h3>
                        <p className="text-slate-500 max-w-md">
                            This workspace is under construction.
                            <br />Expect magic soon.
                        </p>
                    </div>
                )}
            </StudioLayout>

            {/* --- GLOBAL MODALS & OVERLAYS --- */}

            {/* Content Modal (used by AI tools if we re-integrate them, or legacy props) */}
            {modalContent && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm animate-fade-in" onClick={() => setModalContent(null)}>
                    <div className="bg-white w-full max-w-4xl max-h-[85vh] rounded-[32px] flex flex-col shadow-2xl relative" onClick={e => e.stopPropagation()}>
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 rounded-t-[32px]">
                            <h3 className="text-lg font-black text-gray-900 uppercase tracking-tight font-['Outfit']">{modalContent.title}</h3>
                            <button onClick={() => setModalContent(null)} className="p-2 bg-white border border-gray-200 rounded-full text-gray-400 hover:text-gray-900 hover:shadow-md transition-all"><X size={20} /></button>
                        </div>
                        <div className="p-8 overflow-y-auto prose prose-gray max-w-none">
                            {typeof modalContent.content === 'string' ? (
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>{modalContent.content}</ReactMarkdown>
                            ) : (
                                <pre className="whitespace-pre-wrap font-mono text-sm">{JSON.stringify(modalContent.content, null, 2)}</pre>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <ConfirmationModal
                isOpen={showDisableConfirm}
                onClose={() => setShowDisableConfirm(false)}
                onConfirm={() => handleTogglePortalStatus(true)}
                title="Disable Portal?"
                message="Are you sure you want to disable this portal? Guests will lose access until re-enabled."
            />

            {/* URL Import Modal */}
            {showUrlModal && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm animate-fade-in" onClick={() => setShowUrlModal(false)}>
                    <div className="bg-white w-full max-w-lg rounded-[32px] flex flex-col shadow-2xl relative" onClick={e => e.stopPropagation()}>
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 rounded-t-[32px]">
                            <h3 className="text-lg font-black text-gray-900 uppercase tracking-tight font-['Outfit']">Import from URL</h3>
                            <button onClick={() => setShowUrlModal(false)} className="p-2 bg-white border border-gray-200 rounded-full text-gray-400 hover:text-gray-900 hover:shadow-md transition-all"><X size={20} /></button>
                        </div>
                        <div className="p-8">
                            <form onSubmit={submitUrlUpload}>
                                <div className="mb-6">
                                    <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">Video or Audio Link</label>
                                    <input
                                        type="url"
                                        name="urlInput"
                                        placeholder="https://youtube.com/watch?v=..."
                                        className="w-full bg-gray-50 border border-gray-200 rounded-2xl px-5 py-4 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-medium placeholder-gray-400"
                                        autoFocus
                                        required
                                    />
                                </div>
                                <div className="flex justify-end gap-3">
                                    <button type="button" onClick={() => setShowUrlModal(false)} className="px-6 py-3 bg-white border border-gray-200 text-gray-600 font-bold rounded-xl hover:bg-gray-50 transition-all">Cancel</button>
                                    <button type="submit" disabled={isUploadingMaster} className="px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl shadow-lg shadow-indigo-200 active:scale-95 transition-all flex items-center gap-2">
                                        {isUploadingMaster ? <Loader size={18} className="animate-spin" /> : <Link size={18} />}
                                        <span>{isUploadingMaster ? 'Processing...' : 'Import Media'}</span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* HOST CHAT FLOATING BUBBLE */}
            {guestPortal?.portal?.status === 'active' && (
                <div className="fixed bottom-6 right-6 z-[60] flex flex-col items-end">
                    {/* Chat Window */}
                    <div className={`
                        mb-4 bg-white rounded-[24px] shadow-2xl border border-gray-100 overflow-hidden transition-all duration-300 origin-bottom-right
                        ${isChatOpen ? 'w-[350px] h-[500px] opacity-100 scale-100 pointer-events-auto' : 'w-0 h-0 opacity-0 scale-90 pointer-events-none'}
                    `}>
                        <div className="h-full flex flex-col bg-gray-50/50">
                            {/* Header */}
                            <div className="p-4 bg-white border-b border-gray-100 flex items-center justify-between shadow-sm z-10">
                                <div className="flex items-center gap-3">
                                    <div className="relative">
                                        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold overflow-hidden">
                                            {headshotUrl ? <img src={headshotUrl} alt="Guest" className="w-full h-full object-cover" /> : <User size={20} />}
                                        </div>
                                        <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></div>
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-gray-900 text-sm">{guestName || 'Guest'}</h4>
                                        <p className="text-xs text-gray-400 font-medium">Studio Chat</p>
                                    </div>
                                </div>
                                <button onClick={() => setIsChatOpen(false)} className="p-2 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 transition-colors">
                                    <X size={18} />
                                </button>
                            </div>

                            {/* Messages Area */}
                            <div className="flex-1 overflow-y-auto p-4 bg-gradient-to-b from-slate-50 to-white/50" ref={hostChatContainerRef}>
                                <div className="min-h-full flex flex-col justify-end space-y-4">
                                    {(!guestPortal.portal.messages || guestPortal.portal.messages.length === 0) ? (
                                        <div className="flex-1 flex flex-col items-center justify-center text-center opacity-40 p-6">
                                            <MessageSquare size={32} className="mb-2" />
                                            <p className="text-sm font-medium">No messages yet.<br />Start the conversation!</p>
                                        </div>
                                    ) : (
                                        guestPortal.portal.messages.map((m, i) => (
                                            <div key={i} className={`flex ${m.sender === 'host' ? 'justify-end' : 'justify-start'}`}>
                                                <div className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm leading-relaxed shadow-sm ${m.sender === 'host'
                                                    ? 'bg-indigo-600 text-white rounded-br-none'
                                                    : 'bg-white text-gray-800 border border-gray-100 rounded-bl-none'
                                                    }`}>
                                                    {m.text}
                                                </div>
                                            </div>
                                        ))
                                    )}
                                    <div ref={hostChatEndRef} />
                                </div>
                            </div>

                            {/* Input Area */}
                            <div className="p-3 bg-white border-t border-gray-100">
                                <form
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        handleSendHostMessage();
                                    }}
                                    className="flex items-center gap-2 bg-gray-50 rounded-xl p-1.5 focus-within:ring-2 focus-within:ring-indigo-100 transition-all border border-transparent focus-within:border-indigo-200"
                                >
                                    <input
                                        type="text"
                                        placeholder="Type a message..."
                                        className="flex-1 bg-transparent border-none focus:ring-0 px-3 py-2 text-sm font-medium text-gray-900 placeholder:text-gray-400"
                                        value={hostMessage}
                                        onChange={(e) => setHostMessage(e.target.value)}
                                        disabled={sendingHostMessage}
                                    />
                                    <button
                                        type="submit"
                                        disabled={!hostMessage.trim() || sendingHostMessage}
                                        className={`p-2.5 rounded-lg transition-all active:scale-95 ${!hostMessage.trim() || sendingHostMessage
                                            ? 'bg-gray-200 text-gray-400'
                                            : 'bg-indigo-600 text-white shadow-md shadow-indigo-200 hover:bg-indigo-700'
                                            }`}
                                    >
                                        {sendingHostMessage ? <Loader size={16} className="animate-spin" /> : <Send size={16} />}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>

                    {/* Floating Toggle Button */}
                    <div className="relative group">
                        <button
                            onClick={() => setIsChatOpen(!isChatOpen)}
                            className={`w-14 h-14 rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110 active:scale-95 border-2 border-white
                                ${isChatOpen ? 'bg-gray-900 text-white rotate-90' : 'bg-indigo-600 text-white'}`}
                        >
                            {isChatOpen ? <X size={24} /> : <MessageSquare size={24} />}
                        </button>
                        {/* Unread Badge */}
                        {!isChatOpen && guestPortal?.portal?.messages?.length > 0 && guestPortal.portal.messages[guestPortal.portal.messages.length - 1].sender === 'guest' && (
                            <span className="absolute top-0 right-0 w-4 h-4 bg-red-500 border-2 border-white rounded-full animate-bounce"></span>
                        )}
                    </div>
                </div>
            )}

            {/* NEW TRANSCRIPTION MODAL (Keep accessible for Phase 2/Legacy) */}
            <TranscriptionModal
                isOpen={showTranscriptionModal}
                onClose={() => setShowTranscriptionModal(false)}
                job={transcriptionJob}
                onCopyToClipboard={(text) => {
                    navigator.clipboard.writeText(text);
                    alert("Copied to clipboard!");
                }}
            />
        </>
    );
};

export default EpisodePage;